import type { OwnerKind } from "@coach/shared";
import type { LocalDb } from "@coach/sync";
import {
  type BoardSnapshot,
  boardDimensions,
  emptyScene,
  type FieldKind,
  isFieldKind,
  isOrientation,
  isTokenSizePreset,
  legacyBoardDimensions,
  type NameDisplay,
  type Orientation,
  type Scene,
  scaleSceneToBoard,
  type TokenSizePreset,
} from "../features/tactics/scene.js";
import { enqueueOp, newId, nowIso, selectAll, withLocalWrite } from "./base.js";

/**
 * Board repository — persistence for the tactical board.
 *
 * Boards are polymorphic by owner. Default in v2 is `ownerKind="coach"`
 * with `ownerId=<userId>` (a personal scratchpad). Team boards are
 * `ownerKind="team"` with `ownerId=<teamId>` and visible to every coach
 * assigned to the team.
 *
 * One row holds a complete `BoardSnapshot` (field config + an ordered
 * list of animation frames) as JSON in `snapshot_json`.
 */

export interface BoardRow {
  id: string;
  org_id: string;
  rev: number | null;
  updated_at: string;
  deleted_at: string | null;
  client_id: string | null;
  owner_kind: string;
  owner_id: string;
  kind: string;
  name: string;
  snapshot_json: string;
  [key: string]: string | number | null;
}

export interface BoardSummary {
  id: string;
  name: string;
  updatedAt: string;
  fieldKind: FieldKind;
  orientation: Orientation;
  frameCount: number;
  cover: string | null;
}

function isNameDisplay(value: unknown): value is NameDisplay {
  return value === "full" || value === "short";
}

/**
 * Read a stored snapshot, normalising it into the current coordinate space.
 *
 * This is the single read path for board snapshots — both `summarize()` and
 * the editor route go through it — which makes it the right place for the
 * legacy-geometry migration.
 *
 * Snapshots written before the board became DIN-proportioned carry no
 * `boardGeometry` field. Their coordinates live in the old 16:9 space, so
 * every frame is rescaled into the current board and the field is stamped.
 * Nothing is written back here: the migrated snapshot reaches the database on
 * the user's next regular save.
 *
 * The stamp is what keeps this idempotent, so **every** field of the snapshot
 * has to survive this function. Rebuilding a partial object here would drop
 * the flag and rescale the same scene again on every single load.
 */
export function parseSnapshot(json: string): BoardSnapshot {
  try {
    const data = JSON.parse(json) as Partial<BoardSnapshot>;
    const fieldKind = isFieldKind(data.fieldKind) ? data.fieldKind : "pitch_green";
    const orientation = isOrientation(data.orientation) ? data.orientation : "landscape";
    const frames =
      Array.isArray(data.frames) && data.frames.length > 0
        ? data.frames
        : [{ id: newId(), durationMs: 1500, scene: emptyScene() }];
    const cover = typeof data.cover === "string" && data.cover.length > 0 ? data.cover : null;
    const tokenSizePreset: TokenSizePreset | undefined = isTokenSizePreset(data.tokenSizePreset)
      ? data.tokenSizePreset
      : undefined;
    const nameDisplay: NameDisplay | undefined = isNameDisplay(data.nameDisplay)
      ? data.nameDisplay
      : undefined;

    const snapshot: BoardSnapshot = {
      fieldKind,
      orientation,
      frames,
      cover,
      boardGeometry: "din",
      ...(tokenSizePreset ? { tokenSizePreset } : {}),
      ...(nameDisplay ? { nameDisplay } : {}),
    };

    if (data.boardGeometry === "din") return snapshot;

    const from = legacyBoardDimensions(orientation, fieldKind);
    const to = boardDimensions(orientation, fieldKind);
    return {
      ...snapshot,
      frames: frames.map((f) => ({ ...f, scene: scaleSceneToBoard(f.scene, from, to) })),
    };
  } catch {
    return {
      fieldKind: "pitch_green",
      orientation: "landscape",
      frames: [{ id: newId(), durationMs: 1500, scene: emptyScene() }],
      cover: null,
      boardGeometry: "din",
    };
  }
}

export function summarize(row: BoardRow): BoardSummary {
  const snap = parseSnapshot(row.snapshot_json);
  return {
    id: row.id,
    name: row.name,
    updatedAt: row.updated_at,
    fieldKind: snap.fieldKind,
    orientation: snap.orientation,
    frameCount: snap.frames.length,
    cover: snap.cover ?? null,
  };
}

export type BoardKind = "taktik" | "whiteboard" | "note";

export async function listBoards(
  db: LocalDb,
  orgId: string,
  ownerId: string,
  ownerKind: OwnerKind = "coach",
  kind: BoardKind = "taktik",
): Promise<BoardRow[]> {
  return selectAll<BoardRow>(
    db,
    `SELECT * FROM board
       WHERE org_id = ? AND owner_kind = ? AND owner_id = ?
         AND kind = ? AND deleted_at IS NULL
       ORDER BY updated_at DESC`,
    [orgId, ownerKind, ownerId, kind],
  );
}

export async function getBoard(db: LocalDb, id: string): Promise<BoardRow | null> {
  const rows = await selectAll<BoardRow>(
    db,
    `SELECT * FROM board WHERE id = ? AND deleted_at IS NULL LIMIT 1`,
    [id],
  );
  return rows[0] ?? null;
}

export interface CreateBoardInput {
  orgId: string;
  ownerId: string;
  ownerKind?: OwnerKind;
  kind?: BoardKind;
  name: string;
  /** Tactics `BoardSnapshot` or any whiteboard snapshot shape. */
  snapshot: unknown;
}

export async function createBoard(db: LocalDb, input: CreateBoardInput): Promise<BoardRow> {
  const id = newId();
  const updatedAt = nowIso();
  const ownerKind = input.ownerKind ?? "coach";
  const kind = input.kind ?? "taktik";
  const snapshotJson = JSON.stringify(input.snapshot);

  const row: BoardRow = {
    id,
    org_id: input.orgId,
    rev: null,
    updated_at: updatedAt,
    deleted_at: null,
    client_id: null,
    owner_kind: ownerKind,
    owner_id: input.ownerId,
    kind,
    name: input.name,
    snapshot_json: snapshotJson,
  };

  await withLocalWrite(db, async (tx) => {
    await tx.exec(
      `INSERT INTO board
         (id, org_id, rev, updated_at, deleted_at, client_id, owner_kind, owner_id, kind, name, snapshot_json)
         VALUES (?, ?, NULL, ?, NULL, NULL, ?, ?, ?, ?, ?)`,
      [id, input.orgId, updatedAt, ownerKind, input.ownerId, kind, input.name, snapshotJson],
    );
    await enqueueOp(tx, {
      entity: "board",
      kind: "upsert",
      payload: {
        id,
        orgId: input.orgId,
        ownerKind,
        ownerId: input.ownerId,
        kind,
        name: input.name,
        snapshotJson,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: null,
    });
  });

  return row;
}

export interface UpdateBoardInput {
  id: string;
  name?: string;
  snapshot?: unknown;
}

export async function updateBoard(db: LocalDb, input: UpdateBoardInput): Promise<void> {
  const updatedAt = nowIso();

  await withLocalWrite(db, async (tx) => {
    const existing = (
      await tx.query<BoardRow>(`SELECT * FROM board WHERE id = ? LIMIT 1`, [input.id])
    )[0];
    if (!existing) throw new Error(`board ${input.id} not found`);

    const name = input.name ?? existing.name;
    const snapshotJson = input.snapshot ? JSON.stringify(input.snapshot) : existing.snapshot_json;

    await tx.exec(
      `UPDATE board
         SET name = ?, snapshot_json = ?, updated_at = ?
         WHERE id = ?`,
      [name, snapshotJson, updatedAt, input.id],
    );
    await enqueueOp(tx, {
      entity: "board",
      kind: "upsert",
      payload: {
        id: input.id,
        orgId: existing.org_id,
        ownerKind: existing.owner_kind,
        ownerId: existing.owner_id,
        kind: existing.kind,
        name,
        snapshotJson,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: existing.rev,
    });
  });
}

export async function deleteBoard(db: LocalDb, args: { id: string; orgId: string }): Promise<void> {
  const updatedAt = nowIso();
  await withLocalWrite(db, async (tx) => {
    const existing = (
      await tx.query<BoardRow>(`SELECT * FROM board WHERE id = ? LIMIT 1`, [args.id])
    )[0];
    if (!existing) return;

    await tx.exec(`UPDATE board SET deleted_at = ?, updated_at = ? WHERE id = ?`, [
      updatedAt,
      updatedAt,
      args.id,
    ]);
    await enqueueOp(tx, {
      entity: "board",
      kind: "delete",
      payload: { id: args.id, orgId: args.orgId, updatedAt },
      updatedAt,
      baseRev: existing.rev,
    });
  });
}

/** Pure helper — convenient when callers only have a `Scene` not a full snapshot. */
export function snapshotWithScene(
  base: BoardSnapshot,
  frameId: string,
  scene: Scene,
): BoardSnapshot {
  return {
    ...base,
    frames: base.frames.map((f) => (f.id === frameId ? { ...f, scene } : f)),
  };
}
